import { useEffect, useRef } from 'react';
import type { KnowledgeBaseDocument } from '../../../types/knowledge';
import type {
  DocumentEmbeddingProgress,
  DocumentSortField,
  WithResourceId,
} from '../../../types/knowledge';
import { KnowledgeDocumentTableActions } from '../KnowledgeDocumentTableActions';
import { KnowledgeDocumentTableRow } from './KnowledgeDocumentTableRow';
import { KnowledgeDocumentsPlainHeader } from './KnowledgeDocumentsPlainHeader';
import { KnowledgeDocumentsSortableHeader } from './KnowledgeDocumentsSortableHeader';

type DocumentRow = WithResourceId<KnowledgeBaseDocument>;

type KnowledgeDocumentsTableProps = {
  documents: DocumentRow[];
  locale: string;
  canManageDocuments: boolean;
  documentSort: {
    column: DocumentSortField;
    direction: 'ASC' | 'DESC';
  };
  selectedDocumentIds: Set<string>;
  selectedDocuments: DocumentRow[];
  hasSelection: boolean;
  allVisibleSelected: boolean;
  someVisibleSelected: boolean;
  isBulkActionRunning: boolean;
  embeddingProgressByDocumentId: Map<string, DocumentEmbeddingProgress>;
  publishState: 'PUBLISHED' | 'STAGING';
  translate: (key: string, params?: Record<string, string>) => string;
  onDocumentSortToggle: (column: DocumentSortField) => void;
  onToggleSelectAllVisible: () => void;
  onToggleDocumentSelected: (documentId: string) => void;
  onPreview: (documents: DocumentRow[]) => void;
  onDownloadDocuments: (documents: DocumentRow[]) => void;
  onResumeAbandonedDocuments: (documents: DocumentRow[]) => void;
  onRetryDocuments: (documents: DocumentRow[]) => void;
  onDeleteDocuments: (documents: DocumentRow[]) => void;
  onDownload: (document: DocumentRow) => void;
  onResumeAbandonedDocument: (document: DocumentRow) => void;
};

export function KnowledgeDocumentsTable({
  documents,
  locale,
  canManageDocuments,
  documentSort,
  selectedDocumentIds,
  selectedDocuments,
  hasSelection,
  allVisibleSelected,
  someVisibleSelected,
  isBulkActionRunning,
  embeddingProgressByDocumentId,
  publishState,
  translate,
  onDocumentSortToggle,
  onToggleSelectAllVisible,
  onToggleDocumentSelected,
  onPreview,
  onDownloadDocuments,
  onResumeAbandonedDocuments,
  onRetryDocuments,
  onDeleteDocuments,
  onDownload,
  onResumeAbandonedDocument,
}: KnowledgeDocumentsTableProps) {
  const selectAllRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someVisibleSelected;
    }
  }, [someVisibleSelected]);

  return (
    <div className="knowledge-database__table-wrap liks-scrollbar">
      <table className="knowledge-database__table">
        <thead>
          <tr>
            <th
              scope="col"
              className="knowledge-database__select-cell knowledge-database__col-select"
            >
              <input
                ref={selectAllRef}
                type="checkbox"
                className="knowledge-database__select-checkbox"
                checked={allVisibleSelected}
                aria-label={translate('knowledge.table.selectAll')}
                onChange={onToggleSelectAllVisible}
              />
            </th>
            <KnowledgeDocumentsSortableHeader
              className="knowledge-database__col-file"
              label={translate('knowledge.table.fileName')}
              column="filename"
              activeColumn={documentSort.column}
              direction={documentSort.direction}
              translate={translate}
              onSortToggle={onDocumentSortToggle}
            />
            <KnowledgeDocumentsSortableHeader
              className="knowledge-database__col-size"
              label={translate('knowledge.table.size')}
              column="filesize"
              activeColumn={documentSort.column}
              direction={documentSort.direction}
              translate={translate}
              onSortToggle={onDocumentSortToggle}
            />
            <KnowledgeDocumentsPlainHeader
              className="knowledge-database__col-type"
              label={translate('knowledge.table.fileType')}
            />
            <KnowledgeDocumentsPlainHeader
              className="knowledge-database__col-status"
              label={translate('knowledge.table.status')}
            />
            <KnowledgeDocumentsSortableHeader
              className="knowledge-database__col-uploaded"
              label={translate('knowledge.table.uploadedAt')}
              column="created"
              activeColumn={documentSort.column}
              direction={documentSort.direction}
              translate={translate}
              onSortToggle={onDocumentSortToggle}
            />
            <KnowledgeDocumentsSortableHeader
              className="knowledge-database__col-modified"
              label={translate('knowledge.table.modifiedAt')}
              column="modified"
              activeColumn={documentSort.column}
              direction={documentSort.direction}
              translate={translate}
              onSortToggle={onDocumentSortToggle}
            />
            <KnowledgeDocumentsPlainHeader
              className="knowledge-database__col-source"
              label={translate('knowledge.table.source')}
            />
            <KnowledgeDocumentsPlainHeader
              className="knowledge-database__col-url"
              label={translate('knowledge.table.url')}
            />
            <th
              scope="col"
              className={[
                'knowledge-database__actions-cell',
                'knowledge-database__col-actions',
                hasSelection && 'knowledge-database__actions-cell--bulk',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {hasSelection ? (
                <KnowledgeDocumentTableActions
                  mode="bulk"
                  documents={selectedDocuments}
                  canManageDocuments={canManageDocuments}
                  disabled={isBulkActionRunning}
                  translate={translate}
                  onPreview={onPreview}
                  onDownload={onDownloadDocuments}
                  onResume={onResumeAbandonedDocuments}
                  onRetry={onRetryDocuments}
                  onDelete={onDeleteDocuments}
                />
              ) : (
                <div className="knowledge-database__table-header">
                  <span>{translate('knowledge.table.actions')}</span>
                </div>
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {documents.map((document) => {
            const status = document.status ?? 'PENDING';
            const embeddingProgress =
              status === 'EMBEDDING' && publishState === 'PUBLISHED'
                ? embeddingProgressByDocumentId.get(document.id)
                : undefined;

            return (
              <KnowledgeDocumentTableRow
                key={document.id}
                document={document}
                locale={locale}
                canManageDocuments={canManageDocuments}
                isSelected={selectedDocumentIds.has(document.id)}
                hasSelection={hasSelection}
                isBulkActionRunning={isBulkActionRunning}
                embeddingProgress={embeddingProgress}
                translate={translate}
                onToggleSelected={onToggleDocumentSelected}
                onPreview={onPreview}
                onDownload={onDownload}
                onResume={onResumeAbandonedDocument}
                onRetry={onRetryDocuments}
                onDelete={onDeleteDocuments}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
